import { useFieldArray, useForm, useWatch } from "react-hook-form";
import ConfirmationModal from "../../../../ui/Modal/ConfirmationModal";
import Modal from "../../../../ui/Modal/Modal";
import { graphql, DocumentType } from "../../../../__generated__/gql";
import { useMutation, useQuery } from "urql";
import useUser from "../../../../providers/UserProvider/useUser";
import TeamCard from "../../../../components/TeamCard/TeamCard";
import { BillingTier } from "../../../../__generated__/gql/graphql";
import styles from "./LinkTeamsModal.module.scss";
import { Close, Plus } from "../../../../ui/Icon/Icon";
import IconButton from "../../../../ui/IconButton/IconButton";
import Badge from "../../../../ui/Badge/Badge";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import TeamSelector from "../../../../components/TeamSelector/TeamSelector";
import { billingTierLabel } from "../../../../util/billing";

const LinkTeamsComponentQuery = graphql(/* GraphQL */ `
  query linkTeamsComponent {
    ...TeamSelectorQuery
    tenant {
      id
      linkedTenants {
        id
        name
        billingTier
        memberCount
      }
    }
    memberships {
      id
      linkedTenants {
        id
      }
      parentTenant {
        id
      }
    }
  }
`);

const LinkTeamsMutation = graphql(/* GraphQL */ `
  mutation linkTeams($input: LinkTenantsInput!) {
    linkTenants(input: $input) {
      id
      linkedTenants {
        id
        name
        billingTier
        memberCount
      }
    }
  }
`);

const schema = z.object({
  teams: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      billingTier: z.nativeEnum(BillingTier),
      memberCount: z.number(),
    }),
  ),
});

type FormData = z.infer<typeof schema>;

function InnerLinkTeamsModal({
  onClose,
  onSubmit,
  data,
}: {
  onClose: () => void;
  onSubmit: (data: FormData) => Promise<void>;
  data?: DocumentType<typeof LinkTeamsComponentQuery>;
}) {
  const { billingInfo, tenant } = useUser();
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isSubmitted, isDirty },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      teams: data?.tenant.linkedTenants?.map((team) => ({
        id: team.id,
        name: team.name,
        billingTier: team.billingTier,
        memberCount: team.memberCount,
      })),
    },
  });

  const teams = useWatch({ control, name: "teams" });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "teams",
  });

  if (!tenant || !billingInfo) {
    return null;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.teamsWrapper}>
        <div className={styles.teams}>
          <div className={styles.teamCard}>
            <TeamCard
              name={tenant.name}
              billingTier={billingInfo.billingTier}
              memberCount={tenant.memberCount}
            />
            <Badge
              variant="badge"
              color="outline"
              showDot={false}
              size="medium"
            >
              Primary
            </Badge>
          </div>

          {fields.map((field, index) => (
            <div key={field.id} className={styles.teamCard}>
              <TeamCard
                name={field.name}
                billingTier={field.billingTier}
                memberCount={field.memberCount}
              />
              <IconButton
                IconComponent={Close}
                onClick={() => remove(index)}
                size="small"
                iconSize="small"
                aria-label="Remove team"
              />
            </div>
          ))}
        </div>
      </div>

      <div className={styles.bottomSection}>
        {teams.length === billingInfo.linkedTeamsLimit + 1 ? (
          <div className={styles.linkedTeamsLimit}>
            Maximum of {billingInfo.linkedTeamsLimit}{" "}
            {billingInfo.linkedTeamsLimit === 1 ? "team" : "teams"} reached for{" "}
            {billingTierLabel[billingInfo.billingTier]} Plan
          </div>
        ) : (
          <TeamSelector
            data={data}
            onClick={(team) => append(team)}
            showChevron={false}
            filter={(team) => {
              const membership = data?.memberships.find(
                (m) => m.id === team.id,
              );

              if (
                membership?.parentTenant &&
                tenant.id !== membership.parentTenant.id
              ) {
                return false;
              }

              if (membership?.linkedTenants?.length) {
                return false;
              }

              return (
                team.id !== tenant.id &&
                !teams.some((t) => t.id === team.id) &&
                team.billingTier === BillingTier.Free
              );
            }}
          >
            <div className={styles.addButton}>
              <Plus size="small" /> Add linked team
            </div>
          </TeamSelector>
        )}
      </div>
      <ConfirmationModal
        onClose={onClose}
        confirmDisabled={!isDirty}
        confirmationText={
          !data?.tenant.linkedTenants?.length ? "Link teams" : "Save"
        }
        loading={isSubmitting || isSubmitted}
      />
    </form>
  );
}

function LinkTeamsModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [linkTeamsQueryResponse] = useQuery({ query: LinkTeamsComponentQuery });
  const [, linkTeamsMutation] = useMutation(LinkTeamsMutation);

  const onSubmit = async (data: FormData) => {
    await linkTeamsMutation({
      input: {
        tenantIds: data.teams.map((team) => team.id),
      },
    });

    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xsmall"
      title="Link teams"
      subtitle="Linked teams allow you to share your credits between multiple teams."
      loading={linkTeamsQueryResponse.fetching}
    >
      <InnerLinkTeamsModal
        onClose={onClose}
        onSubmit={onSubmit}
        data={linkTeamsQueryResponse.data}
      />
    </Modal>
  );
}

export default LinkTeamsModal;
